import React, { useState, useMemo } from 'react';
import { Filter, Download, X } from 'lucide-react';

import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { PayerSelector } from './PayerSelector';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { PayerSummary, AdminPaymentMethodSummary } from '../lib/api/types';

/**
 * Utility function to convert dates to ISO 8601 format
 * The API requires ISO 8601 formatted date strings for filtering transactions.
 * This function:
 * 1. Converts YYYY-MM-DD format from date inputs to ISO 8601 format
 * 2. Sets time boundaries (00:00:00 for start date, 23:59:59 for end date)
 * 3. Uses UTC timezone to ensure consistency
 *
 * @param dateStr - Date string in YYYY-MM-DD format from date input
 * @param isEndOfDay - Whether to set time to end of day (23:59:59)
 * @returns ISO 8601 formatted date string or undefined if input is empty
 */
export const formatDateToISO = (dateStr: string, isEndOfDay: boolean = false): string | undefined => {
  if (!dateStr) return undefined;

  // Parse the YYYY-MM-DD string into a Date object
  const date = new Date(dateStr);

  // Set time to beginning of day (00:00:00) or end of day (23:59:59) in UTC
  if (isEndOfDay) {
    date.setUTCHours(23, 59, 59, 999);
  } else {
    date.setUTCHours(0, 0, 0, 0);
  }

  // Return the date in ISO 8601 format
  return date.toISOString();
};

export interface ProcessedTransactionFilters {
  payerId?: string;
  paymentMethodId?: string;
  status?: 'COMPLETED' | 'FAILED' | 'PENDING' | 'AUTHORIZED' | 'CAPTURED' | 'VOIDED' | '';
  startDate?: string;
  endDate?: string;
  isReversed?: boolean;
}

interface ProcessedTransactionFiltersProps {
  filters: ProcessedTransactionFilters;
  onFiltersChange: (filters: ProcessedTransactionFilters) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
}

export const ProcessedTransactionFiltersComponent: React.FC<ProcessedTransactionFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  onDownloadCSV,
  isLoading = false,
}) => {
  const [selectedPayer, setSelectedPayer] = useState<PayerSummary | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<AdminPaymentMethodSummary | null>(null);

  const handleInputChange = (field: keyof ProcessedTransactionFilters, value: string | number) => {
    onFiltersChange({
      ...filters,
      [field]: value === '' ? undefined : value,
    });
  };

  const handlePayerSelect = (payer: PayerSummary | null) => {
    setSelectedPayer(payer);
    // Clear payment method when payer changes
    setSelectedPaymentMethod(null);
    onFiltersChange({
      ...filters,
      payerId: payer?.PayerAccountId || undefined,
      paymentMethodId: undefined,
    });
  };

  const handlePaymentMethodSelect = (paymentMethod: AdminPaymentMethodSummary | null) => {
    setSelectedPaymentMethod(paymentMethod);
    onFiltersChange({
      ...filters,
      paymentMethodId: paymentMethod?.PaymentMethodId || undefined,
    });
  };

  const hasActiveFilters = Object.values(filters).some(value =>
    value !== undefined && value !== '' && value !== null
  );

  const handleClearFilters = () => {
    setSelectedPayer(null);
    setSelectedPaymentMethod(null);
    onClearFilters();
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Filter Transactions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* First Row: Payer ID and Payment Method ID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <PayerSelector
                  selectedPayerId={selectedPayer?.PayerAccountId}
                  onPayerSelect={handlePayerSelect}
                />
              </div>

              <div className="space-y-2">
                <PaymentMethodSelector
                  selectedPayerId={selectedPayer?.PayerAccountId}
                  selectedPaymentMethodId={selectedPaymentMethod?.PaymentMethodId}
                  onPaymentMethodSelect={handlePaymentMethodSelect}
                />
              </div>
        </div>

        {/* Second Row: Status, Reversal Status, and Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              className="w-full p-2 border rounded-md bg-background"
              value={filters.status || ''}
              onChange={(e) => handleInputChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="COMPLETED">COMPLETED</option>
              <option value="AUTHORIZED">AUTHORIZED</option>
              <option value="CAPTURED">CAPTURED</option>
              <option value="VOIDED">VOIDED</option>
              <option value="FAILED">FAILED</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="isReversed">Reversal Status</Label>
            <select
              id="isReversed"
              className="w-full p-2 border rounded-md bg-background"
              value={filters.isReversed === undefined ? '' : filters.isReversed.toString()}
              onChange={(e) => {
                const value = e.target.value;
                onFiltersChange({
                  ...filters,
                  isReversed: value === '' ? undefined : value === 'true',
                });
              }}
            >
              <option value="">All Transactions</option>
              <option value="true">Reversed Only</option>
              <option value="false">Not Reversed</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="endDate">End Date</Label>
            <Input
              id="endDate"
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
            />
          </div>
        </div>

        {/* Note: Minimum and Maximum Amount filters removed */}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 border-t">
          <Button
            onClick={onApplyFilters}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Apply Filters
          </Button>

          {hasActiveFilters && (
            <Button
              variant="outline"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          )}

          <Button
            variant="secondary"
            onClick={onDownloadCSV}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
